import axios, { Canceler } from 'axios';
import { orderBy, uniq } from 'lodash';
import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { AbilitySummary, LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    Ability,
    ApiRequests,
    ApiAdapterUtils,
    characterActions,
    CharacterPreferences,
    CharacterThemeColorContract,
    CharClass,
    rulesEngineSelectors,
    DecorationInfo,
    DecorationUtils,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';

import { SidebarSubheading } from '../../../../components/Sidebar';
import DataLoadingStatusEnum from '../../../../constants/DataLoadingStatusEnum';
import { characterRollContextSelectors } from '../../../../selectors';
import { SharedAppState } from '../../../../stores/typings';
import { AppLoggerUtils } from '../../../../utils';
import DecorationPreviewItem from '../DecorationPreviewItem';

interface ThemeGroupInfo {
    label: string;
    themes: Array<CharacterThemeColorContract>;
}

interface Props extends DispatchProp {
    decorationInfo: DecorationInfo;
    startingClass: CharClass | null;
    abilities: Array<Ability>;
    preferences: CharacterPreferences;
    characterRollContext: IRollContext;
}
interface State {
    themeData: Array<CharacterThemeColorContract>;
    loadingStatus: DataLoadingStatusEnum;
}
class ThemeManager extends React.PureComponent<Props, State> {
    loadThemesCanceler: null | Canceler = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            themeData: [],
            loadingStatus: DataLoadingStatusEnum.NOT_INITIALIZED,
        };
    }

    componentDidMount(): void {
        this.setState({
            loadingStatus: DataLoadingStatusEnum.LOADING,
        });

        ApiRequests.getCharacterGameDataThemeColors({
            cancelToken: new axios.CancelToken((c) => {
                this.loadThemesCanceler = c;
            }),
        })
            .then((response) => {
                let apiData: Array<CharacterThemeColorContract> = [];

                const data = ApiAdapterUtils.getResponseData(response);
                if (data !== null) {
                    apiData = data;
                }

                this.setState({
                    themeData: apiData,
                    loadingStatus: DataLoadingStatusEnum.LOADED,
                });
                this.loadThemesCanceler = null;
            })
            .catch(AppLoggerUtils.handleAdhocApiError);
    }

    componentWillUnmount(): void {
        if (this.loadThemesCanceler !== null) {
            this.loadThemesCanceler();
        }
    }

    handleThemeClick = (theme: CharacterThemeColorContract): void => {
        const { dispatch } = this.props;
        dispatch(characterActions.themeSet(theme));
    };

    handleDefaultThemeClick = (): void => {
        const { dispatch } = this.props;
        dispatch(characterActions.themeSet(null));
    };

    getHighestScoreAbility = (): Ability => {
        const { abilities } = this.props;

        let orderedAbilities = orderBy(
            abilities,
            [(ability) => ability.totalScore, (ability) => ability.label],
            ['desc', 'asc'],
        );

        return orderedAbilities[0];
    };

    renderThemes = (heading: string, themes: Array<CharacterThemeColorContract>): React.ReactNode => {
        const { decorationInfo, preferences, characterRollContext } = this.props;
        const characterTheme = DecorationUtils.getCharacterTheme(decorationInfo);

        let ability = this.getHighestScoreAbility();

        return (
            <div className="ct-decoration-manager__group" key={heading}>
                <SidebarSubheading>{heading}</SidebarSubheading>
                <div className="ct-decoration-manager__list">
                    {themes.map((theme) => {
                        const isSelected =
                            !characterTheme.isDefault && theme.themeColorId === characterTheme.themeColorId;
                        let classNames: Array<string> = ['ct-decoration-manager__item--theme'];
                        if (isSelected) {
                            classNames.push('ct-decoration-manager__item--current-theme');
                        }
                        return (
                            <DecorationPreviewItem
                                key={theme.themeColorId}
                                avatarId={theme?.themeColorId ?? null}
                                isSelected={isSelected}
                                onSelected={() => this.handleThemeClick(theme)}
                                className={classNames.join(' ')}
                                avatarName={theme.name}
                            >
                                <AbilitySummary
                                    theme={DecorationUtils.generateCharacterTheme(theme, preferences)}
                                    ability={ability}
                                    preferences={preferences}
                                    diceEnabled={false}
                                    rollContext={characterRollContext}
                                />
                            </DecorationPreviewItem>
                        );
                    })}
                </div>
            </div>
        );
    };

    renderContent = (): React.ReactNode => {
        const { decorationInfo, preferences, characterRollContext } = this.props;

        const { themeData } = this.state;

        let tags: Array<string> = uniq(
            themeData.reduce((acc: Array<string>, data) => {
                let newTags = data.tags ? data.tags : [];
                return [...acc, ...newTags];
            }, []),
        );
        let groups: Array<ThemeGroupInfo> = [];
        tags.forEach((tag) => {
            groups.push({
                label: tag,
                themes: themeData.filter((data) => data.tags && data.tags.includes(tag)),
            });
        });

        let classNames: Array<string> = ['ct-decoration-manager__item--theme'];
        if (DecorationUtils.isDefaultTheme(decorationInfo)) {
            classNames.push('ct-decoration-manager__item--current-theme');
        }

        return (
            <React.Fragment>
                <div className="ct-decoration-manager__group">
                    <SidebarSubheading>Default</SidebarSubheading>
                    <div className="ct-decoration-manager__list">
                        <DecorationPreviewItem
                            avatarId={null}
                            isSelected={DecorationUtils.isDefaultTheme(decorationInfo)}
                            onSelected={this.handleDefaultThemeClick}
                            className={classNames.join(' ')}
                            avatarName={'DDB Red'}
                        >
                            <AbilitySummary
                                theme={DecorationUtils.generateCharacterTheme(null, preferences)}
                                ability={this.getHighestScoreAbility()}
                                preferences={preferences}
                                diceEnabled={false}
                                rollContext={characterRollContext}
                            />
                        </DecorationPreviewItem>
                    </div>
                </div>
                {groups.map((group) => this.renderThemes(group.label, group.themes))}
            </React.Fragment>
        );
    };

    render() {
        const { loadingStatus } = this.state;

        let contentNode: React.ReactNode;
        if (loadingStatus === DataLoadingStatusEnum.LOADED) {
            contentNode = this.renderContent();
        } else {
            contentNode = <LoadingPlaceholder />;
        }

        return <div className="ct-decoration-manager">{contentNode}</div>;
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        decorationInfo: rulesEngineSelectors.getDecorationInfo(state),
        startingClass: rulesEngineSelectors.getStartingClass(state),
        abilities: rulesEngineSelectors.getAbilities(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
    };
}

export default connect(mapStateToProps)(ThemeManager);
